export type TalentPaymentLifecycleStatus = 'not_started' | 'processing' | 'action_required' | 'paid';

/**
 * T2-7494: the API's talent-only payment status (talentPaymentDisplay).
 *   unpaid             the office has not paid (an office "marked paid" alone does not count)
 *   talent_marked_paid the temp marked a non-Pay Now invoice paid
 *   paid_stripe        Pay Now: the money reached the temp's Stripe Express
 *   paid_manual        Pay Now: the office paid; TempStars is paying by hand
 *   action_required    Pay Now: Stripe Express account broken, or Stripe reported a payout failure
 */
export type TalentPaymentDisplay = 'unpaid' | 'talent_marked_paid' | 'paid_stripe' | 'paid_manual' | 'action_required';

export type TalentPaymentStateInput = {
  paymentLifecycleId?: number | null;
  officePaymentStatus?: 'processing' | 'action_required' | 'paid' | null;
  talentPaymentStatus?: TalentPaymentLifecycleStatus | null;
  talentPaymentMethod?: 'instant' | 'standard' | null;
  talentPaymentDisplay?: TalentPaymentDisplay | null;
  hygienistMarkedPaid?: boolean | number | string | null;
  dentistMarkedPaid?: boolean | number | null;
  /** Talent reads send an office-only claim as officeMarkedPaid, with dentistMarkedPaid false (T2-6244). */
  officeMarkedPaid?: boolean | null;
  paidOn?: string | null;
  totalInvoiceAmt?: number | string | null;
};

export type TalentPaymentViewState = {
  display: TalentPaymentDisplay;
  state: 'unpaid' | 'action_required' | 'paid';
  label: 'UNPAID' | 'ACTION REQUIRED' | 'PAID';
  /** Pay Now PAID has no title, only the badge and the message. */
  title: string | null;
  message: string;
  isPaid: boolean;
  isPayNow: boolean;
  requiresAction: boolean;
  /** "Click here to resolve" opens the temp's Stripe Express (T2-7437 helper). */
  action: 'resolve_stripe' | null;
  /** Mark as Paid exists only outside Pay Now. */
  canUseLegacyPaidToggle: boolean;
  canResendInvoice: boolean;
};

export const TALENT_PAYMENT_COPY = Object.freeze({
  unpaidTitle: 'Payment Not Received',
  unpaidMessage: 'Your payment has not been confirmed as received.',
  paidStripeMessage: 'Your money has been sent to your Stripe Express account.',
  paidManualMessage: 'Keep an eye out, your money is on the way.',
  talentMarkedTitle: 'Payment Received',
  actionRequiredTitle: 'Action required',
  actionRequiredMessage: 'Your payment is on its way. Please visit your Stripe Express account to fix it so you can continue to receive your payouts.',
  resolveButton: 'Click here to resolve',
});

const isMarked = (value: unknown): boolean => value === true || Number(value) > 0;

export function formatTalentPaymentAmount(amount: unknown): string {
  const value = Number(amount);
  if (amount == null || amount === '' || !Number.isFinite(value)) return '';
  try {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(value);
  } catch {
    return `$${value.toFixed(2)}`;
  }
}

/** A Pay Now invoice has a payment record (the API's lifecycle projection). */
export function isPayNowInvoice(invoice?: TalentPaymentStateInput | null): boolean {
  return !!invoice?.paymentLifecycleId || !!invoice?.talentPaymentStatus;
}

/**
 * One source of truth for what the temp sees. Pay Now invoices use the API's
 * talentPaymentDisplay (an older API falls back to "paid once the office
 * paid"). Other invoices count only the temp's own mark; an office mark,
 * paidOn or the office's payment method never makes them PAID.
 */
export function getTalentPaymentDisplay(invoice?: TalentPaymentStateInput | null): TalentPaymentDisplay {
  if (!invoice) return 'unpaid';
  if (!isPayNowInvoice(invoice)) {
    return isMarked(invoice.hygienistMarkedPaid) ? 'talent_marked_paid' : 'unpaid';
  }
  const display = invoice.talentPaymentDisplay;
  if (display === 'unpaid' || display === 'paid_stripe' || display === 'paid_manual' || display === 'action_required') {
    return display;
  }
  return invoice.officePaymentStatus === 'paid' ? 'paid_manual' : 'unpaid';
}

export function getTalentPaymentViewState(
  invoice?: TalentPaymentStateInput | null,
): TalentPaymentViewState {
  const display = getTalentPaymentDisplay(invoice);
  const isPayNow = isPayNowInvoice(invoice);
  const base = {
    display,
    isPayNow,
    action: null,
    canUseLegacyPaidToggle: !isPayNow,
    canResendInvoice: false,
  } as const;

  if (display === 'paid_stripe' || display === 'paid_manual') {
    return {
      ...base,
      state: 'paid',
      label: 'PAID',
      title: null,
      message: display === 'paid_stripe' ? TALENT_PAYMENT_COPY.paidStripeMessage : TALENT_PAYMENT_COPY.paidManualMessage,
      isPaid: true,
      requiresAction: false,
    };
  }
  if (display === 'talent_marked_paid') {
    const amount = formatTalentPaymentAmount(invoice?.totalInvoiceAmt);
    return {
      ...base,
      state: 'paid',
      label: 'PAID',
      title: TALENT_PAYMENT_COPY.talentMarkedTitle,
      message: amount ? `Your payment of ${amount} was received.` : 'Your payment was received.',
      isPaid: true,
      requiresAction: false,
    };
  }
  if (display === 'action_required') {
    return {
      ...base,
      state: 'action_required',
      label: 'ACTION REQUIRED',
      title: TALENT_PAYMENT_COPY.actionRequiredTitle,
      message: TALENT_PAYMENT_COPY.actionRequiredMessage,
      isPaid: false,
      requiresAction: true,
      action: 'resolve_stripe',
    };
  }
  return {
    ...base,
    state: 'unpaid',
    label: 'UNPAID',
    title: TALENT_PAYMENT_COPY.unpaidTitle,
    message: TALENT_PAYMENT_COPY.unpaidMessage,
    isPaid: false,
    requiresAction: false,
    // Resend stays locked once Pay Now owns the invoice, or once the office marked it paid.
    canResendInvoice: !isPayNow && !isMarked(invoice?.dentistMarkedPaid) && !invoice?.officeMarkedPaid,
  };
}
